import React, { useState, useMemo } from "react";
import { Screen } from "@/components";
import { Callout, Foot } from "@/components/ms";
import { QuickActionsWidget } from "./widgets/QuickActionsWidget";
import { LayoutSelector } from "./components/LayoutSelector";
import { useDashboardState } from "./hooks/useDashboardState";
import { scoreModules, LAYOUT_CONFIGS, ModuleId, ScoredModule } from "./layouts";
import { getWidget } from "./widgets/registry";

/** Reading order down the page — the layout engine decides what's in each. */
const SECTION_ORDER = ["hero", "attention", "main", "tax", "footer"];

/** What lives in the 340px rail from 1100px up. Everything else runs full width. */
const ASIDE_MODULES: ModuleId[] = [
  ModuleId.ConnectedExchanges,
  ModuleId.TaxSummary,
  ModuleId.Tips,
  ModuleId.UpgradeCTA,
  ModuleId.AppDownload,
];

export const DashboardScreen = () => {
  // Layout testing state - persist in localStorage
  const [selectedLayout, setSelectedLayout] = useState(() => {
    return localStorage.getItem("dashboardLayoutTest") || "";
  });

  // Save layout selection to localStorage
  const handleLayoutChange = (layout: string) => {
    setSelectedLayout(layout);
    if (layout) {
      localStorage.setItem("dashboardLayoutTest", layout);
    } else {
      localStorage.removeItem("dashboardLayoutTest");
    }
  };

  // Detect user state automatically
  const autoDetectedState = useDashboardState();

  // Use manual layout if selected, otherwise use auto-detected state
  const dashboardContext = useMemo(() => {
    if (selectedLayout && LAYOUT_CONFIGS[selectedLayout]) {
      const config = LAYOUT_CONFIGS[selectedLayout];
      return {
        ...autoDetectedState,
        stage: config.stage,
        overlays: config.overlays || [],
      };
    }
    return autoDetectedState;
  }, [selectedLayout, autoDetectedState]);

  // Score and sort modules based on current context
  const scoredModules: ScoredModule[] = useMemo(() => {
    if (selectedLayout && LAYOUT_CONFIGS[selectedLayout]) {
      // Use pre-defined layout modules
      return LAYOUT_CONFIGS[selectedLayout].modules;
    }
    // Use dynamic scoring
    return scoreModules(dashboardContext);
  }, [selectedLayout, dashboardContext]);

  // Flatten the sections back into one reading order (scores already sorted them
  // within each section); anything in an unknown section falls to the end.
  const orderedModules = useMemo(() => {
    const sections: Record<string, ScoredModule[]> = {};
    scoredModules.forEach((module) => {
      const section = module.layout?.section || "main";
      (sections[section] = sections[section] || []).push(module);
    });
    const known = SECTION_ORDER.flatMap((name) => sections[name] || []);
    const rest = Object.keys(sections)
      .filter((name) => !SECTION_ORDER.includes(name))
      .flatMap((name) => sections[name]);
    return [...known, ...rest];
  }, [scoredModules]);

  const mainModules = orderedModules.filter((m) => !ASIDE_MODULES.includes(m.id));
  const asideModules = orderedModules.filter((m) => ASIDE_MODULES.includes(m.id));

  const renderModule = (module: ScoredModule, index: number) => {
    const WidgetComponent = getWidget(module.id);
    if (!WidgetComponent) return null;

    return <WidgetComponent key={`${module.id}-${index}`} />;
  };

  return (
    <Screen heading="Portfolio" className="px-0 py-0">
      <div className="grid grid-cols-1 items-start gap-[var(--gap)] px-[var(--pad)] py-[var(--gap)] ms1100:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-[var(--gap)]">
          {/* Layout Selector (Dev Tool) */}
          {/* <LayoutSelector
            selectedLayout={selectedLayout}
            onLayoutChange={handleLayoutChange}
          /> */}

          {mainModules.map(renderModule)}

          {/* Quick actions sit under the document on small screens, where there
              is no sidebar to reach them from. */}
          <div className="ms1100:hidden">
            <QuickActionsWidget compact={false} />
          </div>
        </div>

        <aside className="flex flex-col gap-[var(--gap)] ms1100:sticky ms1100:top-[76px]">
          {asideModules.map(renderModule)}

          <Callout>
            Prices refresh every 5 minutes. Tax figures recalculate as sources sync and
            questions are answered.
          </Callout>
        </aside>
      </div>

      <Foot>
        Not tax advice. Figures are computed from the history you imported — check them
        against your own records before you file.
      </Foot>
    </Screen>
  );
};
